import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, CircleMarker, Tooltip, Polygon } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, ShieldCheck } from "lucide-react";

// Fix default Leaflet icon paths issue (avoid missing marker images)
// We use custom divIcons only, so we override defaults.

const CITIES = [
    { name: "Paris", lat: 48.8566, lng: 2.3522, big: true },
    { name: "Lyon", lat: 45.7640, lng: 4.8357, big: true },
    { name: "Marseille", lat: 43.2965, lng: 5.3698, big: true },
    { name: "Bordeaux", lat: 44.8378, lng: -0.5792, big: true },
    { name: "Lille", lat: 50.6292, lng: 3.0573 },
    { name: "Toulouse", lat: 43.6047, lng: 1.4442 },
    { name: "Nice", lat: 43.7102, lng: 7.2620 },
    { name: "Nantes", lat: 47.2184, lng: -1.5536 },
    { name: "Strasbourg", lat: 48.5734, lng: 7.7521 },
    { name: "Rennes", lat: 48.1173, lng: -1.6778 },
    { name: "Montpellier", lat: 43.6108, lng: 3.8767 },
    { name: "Dijon", lat: 47.3220, lng: 5.0415 },
    { name: "Reims", lat: 49.2583, lng: 4.0317 },
    { name: "Rouen", lat: 49.4432, lng: 1.0993 },
    { name: "Tours", lat: 47.3941, lng: 0.6848 },
];

// Simplified France mainland border (metropolitan) — [lat, lng]
const FRANCE_BORDER = [
    [51.05, 2.35],
    [50.90, 3.50],
    [50.70, 4.30],
    [49.90, 5.80],
    [49.45, 6.15],
    [49.20, 6.75],
    [48.95, 7.60],
    [48.20, 7.55],
    [47.55, 7.55],
    [47.00, 6.85],
    [46.50, 6.15],
    [46.10, 6.10],
    [45.90, 6.85],
    [45.15, 6.65],
    [44.50, 6.85],
    [44.10, 7.20],
    [43.75, 7.42],
    [43.55, 6.85],
    [43.30, 5.90],
    [43.10, 5.40],
    [43.35, 4.70],
    [43.55, 3.90],
    [43.10, 3.15],
    [42.60, 3.05],
    [42.45, 2.05],
    [42.75, 1.10],
    [43.00, 0.05],
    [43.10, -1.15],
    [43.35, -1.75],
    [44.10, -1.25],
    [45.55, -1.20],
    [46.15, -1.20],
    [46.85, -2.15],
    [47.30, -2.50],
    [47.60, -3.10],
    [47.90, -4.10],
    [48.35, -4.75],
    [48.65, -4.35],
    [48.75, -3.55],
    [48.90, -1.85],
    [49.30, -1.60],
    [49.72, -1.94],
    [49.63, -0.86],
    [49.85, 0.15],
    [50.05, 1.55],
    [50.65, 1.60],
    [50.95, 1.85],
    [51.05, 2.35],
];

// Convoys travel between cities (fromIndex, toIndex, duration in seconds, initial progress)
const CONVOYS = [
    { from: 0, to: 1, dur: 18, offset: 0 },
    { from: 1, to: 2, dur: 15, offset: 4 },
    { from: 3, to: 5, dur: 12, offset: 8 },
    { from: 0, to: 4, dur: 12, offset: 2 },
    { from: 7, to: 0, dur: 20, offset: 6 },
    { from: 2, to: 6, dur: 10, offset: 12 },
    { from: 0, to: 8, dur: 22, offset: 3 },
    { from: 3, to: 0, dur: 25, offset: 14 },
    { from: 9, to: 7, dur: 8, offset: 5 },
    { from: 10, to: 2, dur: 8, offset: 9 },
    { from: 0, to: 12, dur: 6, offset: 11 },
    { from: 5, to: 10, dur: 11, offset: 13 },
    { from: 4, to: 8, dur: 16, offset: 7 },
    { from: 11, to: 1, dur: 9, offset: 4 },
    { from: 13, to: 0, dur: 8, offset: 1 },
    { from: 14, to: 0, dur: 11, offset: 10 },
    { from: 1, to: 8, dur: 14, offset: 15 },
    { from: 6, to: 10, dur: 14, offset: 2 },
    { from: 2, to: 5, dur: 15, offset: 16 },
    { from: 0, to: 13, dur: 7, offset: 6 },
    { from: 7, to: 5, dur: 13, offset: 17 },
    { from: 9, to: 14, dur: 9, offset: 3 },
    { from: 3, to: 7, dur: 18, offset: 8 },
    { from: 11, to: 8, dur: 12, offset: 18 },
    { from: 4, to: 12, dur: 8, offset: 20 },
    { from: 2, to: 1, dur: 15, offset: 5 },
    { from: 1, to: 11, dur: 9, offset: 19 },
    { from: 5, to: 3, dur: 13, offset: 22 },
    { from: 12, to: 8, dur: 13, offset: 7 },
    { from: 6, to: 2, dur: 10, offset: 21 },
    { from: 8, to: 0, dur: 20, offset: 24 },
    { from: 10, to: 5, dur: 10, offset: 11 },
];

const carSvg = `
<svg width="34" height="34" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg">
  <circle cx="17" cy="17" r="16" fill="#1F6FF0" fill-opacity="0.18">
    <animate attributeName="r" values="14;17;14" dur="1.6s" repeatCount="indefinite" />
    <animate attributeName="fill-opacity" values="0.28;0.05;0.28" dur="1.6s" repeatCount="indefinite" />
  </circle>
  <circle cx="17" cy="17" r="10" fill="#1F6FF0" stroke="#ffffff" stroke-width="2" />
  <path d="M11 19 h12 M12 17 h10 M13 15 h8" stroke="#ffffff" stroke-width="1.3" stroke-linecap="round" />
</svg>`;

const carIcon = L.divIcon({
    className: "cvl-car-icon",
    html: carSvg,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
});

// City pulse icon (radar effect)
function makeCityIcon(big) {
    const size = big ? 60 : 46;
    const ringR = big ? 12 : 9;
    const dotR = big ? 5 : 3.5;
    return L.divIcon({
        className: "cvl-city-icon",
        html: `
<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
  <circle cx="${size / 2}" cy="${size / 2}" r="${ringR}" fill="none" stroke="#1F6FF0" stroke-width="1.5" opacity="0.6">
    <animate attributeName="r" values="${ringR};${size / 2 - 2};${ringR}" dur="2.8s" repeatCount="indefinite" />
    <animate attributeName="opacity" values="0.7;0;0.7" dur="2.8s" repeatCount="indefinite" />
  </circle>
  <circle cx="${size / 2}" cy="${size / 2}" r="${ringR}" fill="none" stroke="#1F6FF0" stroke-width="1" opacity="0.4">
    <animate attributeName="r" values="${ringR};${size / 2 - 2};${ringR}" dur="2.8s" begin="1.4s" repeatCount="indefinite" />
    <animate attributeName="opacity" values="0.6;0;0.6" dur="2.8s" begin="1.4s" repeatCount="indefinite" />
  </circle>
  <circle cx="${size / 2}" cy="${size / 2}" r="${dotR}" fill="#ffffff" stroke="#1F6FF0" stroke-width="1.5" />
</svg>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
}

const cityIconBig = makeCityIcon(true);
const cityIconSmall = makeCityIcon(false);

// Compute current position along the line between two cities based on time
function useConvoyPositions() {
    const [positions, setPositions] = useState(() => computeAll(0));

    useEffect(() => {
        const start = performance.now();
        let raf;
        const tick = () => {
            const t = (performance.now() - start) / 1000;
            setPositions(computeAll(t));
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    return positions;
}

function computeAll(elapsed) {
    return CONVOYS.map((c) => {
        const total = c.dur * 2; // go + return (round-trip loop)
        const t = ((elapsed + c.offset) % total) / c.dur;
        // Ping-pong: 0 → 1 then 1 → 0
        const p = t <= 1 ? t : 2 - t;
        const A = CITIES[c.from];
        const B = CITIES[c.to];
        return {
            lat: A.lat + (B.lat - A.lat) * p,
            lng: A.lng + (B.lng - A.lng) * p,
        };
    });
}

export default function CoverageMap() {
    const positions = useConvoyPositions();

    const routes = useMemo(
        () =>
            CONVOYS.map((c) => [
                [CITIES[c.from].lat, CITIES[c.from].lng],
                [CITIES[c.to].lat, CITIES[c.to].lng],
            ]),
        [],
    );

    return (
        <section
            id="coverage-map"
            data-testid="coverage-map"
            className="relative bg-ink text-white overflow-hidden section-y"
        >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(31,111,240,0.15),transparent_65%)] pointer-events-none" />

            <div className="container-x relative">
                <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
                    <div className="lg:col-span-7">
                        <p className="text-xs uppercase tracking-[0.2em] text-signal font-bold mb-4">
                            — Couverture nationale
                        </p>
                        <h2 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-balance">
                            Nous couvrons
                            <br />
                            <span className="text-signal">
                                toute la France
                            </span>
                            .
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9">
                        <p className="text-white/70 leading-relaxed">
                            De Lille à Nice, de Brest à Strasbourg, nos
                            convoyeurs sillonnent la France en temps réel.
                        </p>
                    </div>
                </div>

                {/* Map wrapper */}
                <div className="relative bg-ink/60 backdrop-blur-sm border border-white/10 overflow-hidden">
                    <div className="absolute top-5 left-5 z-[500] inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/40 px-3 py-1.5">
                        <span className="relative flex w-2 h-2">
                            <span className="absolute inset-0 bg-emerald-400 rounded-full animate-ping" />
                            <span className="relative w-2 h-2 bg-emerald-400 rounded-full" />
                        </span>
                        <span
                            data-testid="live-badge"
                            className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-emerald-300 font-bold"
                        >
                            Convoyeurs en direct
                        </span>
                    </div>

                    <div className="cvl-map-wrapper h-[600px] sm:h-[720px]">
                        <MapContainer
                            center={[46.6, 2.5]}
                            zoom={6}
                            zoomControl={false}
                            attributionControl={false}
                            scrollWheelZoom={false}
                            dragging={true}
                            doubleClickZoom={false}
                            style={{ height: "100%", width: "100%", background: "#0A2E5F" }}
                        >
                            <TileLayer
                                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                                maxZoom={19}
                            />
                            <TileLayer
                                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
                                maxZoom={19}
                                opacity={0.5}
                            />

                            {/* France highlighted */}
                            <Polygon
                                positions={FRANCE_BORDER}
                                pathOptions={{
                                    color: "#1F6FF0",
                                    weight: 2,
                                    fillColor: "#1F6FF0",
                                    fillOpacity: 0.18,
                                }}
                            />

                            {/* Routes with flowing dashes */}
                            {routes.map((r, i) => (
                                <Polyline
                                    key={`route-${i}`}
                                    positions={r}
                                    pathOptions={{
                                        color: "#1F6FF0",
                                        weight: 1.5,
                                        opacity: 0.55,
                                        dashArray: "6 10",
                                        className: "cvl-flow-route",
                                    }}
                                />
                            ))}

                            {/* City pins with radar pulse */}
                            {CITIES.map((c) => (
                                <Marker
                                    key={c.name}
                                    position={[c.lat, c.lng]}
                                    icon={c.big ? cityIconBig : cityIconSmall}
                                    interactive={false}
                                >
                                    <Tooltip
                                        permanent
                                        direction="right"
                                        offset={[c.big ? 14 : 10, 0]}
                                        className="cvl-city-label"
                                    >
                                        {c.name}
                                    </Tooltip>
                                </Marker>
                            ))}

                            {/* Animated cars */}
                            {positions.map((p, i) => (
                                <Marker
                                    key={`car-${i}`}
                                    position={[p.lat, p.lng]}
                                    icon={carIcon}
                                    interactive={false}
                                />
                            ))}
                        </MapContainer>
                    </div>

                    <div className="grid grid-cols-2 border-t border-white/10">
                        {[
                            { icon: MapPin, v: "France entière", label: "Couverture" },
                            { icon: ShieldCheck, v: "100%", label: "Assuré" },
                        ].map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div
                                    key={i}
                                    data-testid={`coverage-stat-${i}`}
                                    className={`p-4 sm:p-6 ${
                                        i < 1 ? "border-r border-white/10" : ""
                                    }`}
                                >
                                    <Icon className="w-4 h-4 text-signal mb-2" />
                                    <div className="font-display font-black text-lg sm:text-2xl text-white leading-tight">
                                        {s.v}
                                    </div>
                                    <div className="text-[10px] sm:text-xs uppercase tracking-wider text-white/50 mt-1">
                                        {s.label}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <style>{`
                .cvl-map-wrapper .leaflet-container {
                    font-family: "Satoshi", sans-serif;
                }
                .cvl-city-label {
                    background: rgba(10, 46, 95, 0.9) !important;
                    color: #ffffff !important;
                    border: 1px solid rgba(31, 111, 240, 0.6) !important;
                    box-shadow: 0 0 12px rgba(31, 111, 240, 0.3) !important;
                    padding: 3px 8px !important;
                    font-size: 11px !important;
                    font-weight: 700 !important;
                    letter-spacing: 0.05em !important;
                    border-radius: 0 !important;
                }
                .cvl-city-label::before {
                    display: none;
                }
                .cvl-car-icon,
                .cvl-city-icon {
                    background: transparent !important;
                    border: none !important;
                }
                .cvl-map-wrapper .leaflet-tile-pane {
                    filter: hue-rotate(210deg) saturate(1.15) brightness(0.75);
                }
                .cvl-flow-route {
                    stroke-dashoffset: 0;
                    animation: cvlFlowDash 2s linear infinite;
                }
                @keyframes cvlFlowDash {
                    to {
                        stroke-dashoffset: -32;
                    }
                }
            `}</style>
        </section>
    );
}
